import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { object, string } from 'yup';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Input } from '../../../../../../CommonComponents/OPL/Input/Input';
import { Modal, ModalProps } from '../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Textarea } from '../../../../../../CommonComponents/OPL/Textarea/Textarea';
import { toast } from '../../../../../../CommonComponents/OPL/Toast/Toast';
import type {
  EssentialRolePredefinedOptionDTO,
  EssentialTeamRoleDTO,
  PredefinedRoleSelectOption,
} from '../../../../../types/goalsDTO';
import { useUpsertEssentialRoleMutation } from '../../api/useUpsertEssentialRoleMutation';

interface AddEditEssentialRoleModalProps extends Omit<ModalProps, 'onRequestClose' | 'role'> {
  teamRole: EssentialTeamRoleDTO;
  predefinedOptions: EssentialRolePredefinedOptionDTO[];
  onRequestClose: () => void;
}

interface FormValues {
  Id: string;
  PredefinedKey: string;
  RoleName: string;
  Name: string;
  Email: string;
  PhoneNumber: string;
  Description: string;
}

const validation = object().shape({
  RoleName: string().trim().required('Role is required.'),
  Name: string().trim().required('Name is required.'),
  Email: string().test(
    'email',
    'Email must be valid',
    value => (value?.trim() ? string().email().isValidSync(value.trim()) : true),
  ),
  PhoneNumber: string().test(
    'phone-number-has-10-digits',
    'You must enter a 10-digit phone number',
    str => (str ? str?.replace(/\D/g, '').length === 10 : true),
  ),
  Description: string(),
});

const isPredefinedOption = (
  o: PredefinedRoleSelectOption | { label: string; value: string } | null,
): o is PredefinedRoleSelectOption => Boolean(o && 'predefinedKey' in o);

export const AddEditEssentialRoleModal = ({
  teamRole,
  predefinedOptions,
  isOpen,
  onRequestClose,
}: AddEditEssentialRoleModalProps) => {
  const {
    mutateAsync: upsertRole,
    error: upsertError,
    isLoading: isUpserting,
  } = useUpsertEssentialRoleMutation();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      Id: teamRole.Id,
      PredefinedKey: teamRole.PredefinedKey ?? '',
      RoleName: teamRole.RoleName ?? '',
      Name: teamRole.Name ?? '',
      Email: teamRole.Email ?? '',
      PhoneNumber: teamRole.PhoneNumber ?? '',
      Description: teamRole.Description ?? '',
    },
    resolver: yupResolver(validation),
  });

  useEffect(() => {
    reset({
      Id: teamRole.Id,
      PredefinedKey: teamRole.PredefinedKey ?? '',
      RoleName: teamRole.RoleName ?? '',
      Name: teamRole.Name ?? '',
      Email: teamRole.Email ?? '',
      PhoneNumber: teamRole.PhoneNumber ?? '',
      Description: teamRole.Description ?? '',
    });
  }, [teamRole, reset]);

  const predefinedSelectOptions: PredefinedRoleSelectOption[] = useMemo(
    () =>
      predefinedOptions.map(option => ({
        label: option.Label,
        value: option.Key,
        predefinedKey: option.Key,
        description: option.Description,
      })),
    [predefinedOptions],
  );

  const watchedPredefinedKey = watch('PredefinedKey');
  const watchedRoleName = watch('RoleName');

  const roleSelectValue = useMemo((): PredefinedRoleSelectOption | { label: string; value: string } | null => {
    if (watchedPredefinedKey) {
      const opt = predefinedSelectOptions.find(o => o.predefinedKey === watchedPredefinedKey);
      if (opt) {
        return opt;
      }
    }
    if (watchedRoleName) {
      return { label: watchedRoleName, value: `custom-${watchedRoleName}` };
    }
    return null;
  }, [predefinedSelectOptions, watchedPredefinedKey, watchedRoleName]);

  const isNewRole = !teamRole.Id;

  const onSubmit = async (values: FormValues) => {
    try {
      await upsertRole({
        Id: values.Id,
        PredefinedKey: values.PredefinedKey?.trim() || null,
        RoleName: values.RoleName.trim(),
        Name: values.Name.trim(),
        Email: values.Email?.trim() || null,
        PhoneNumber: values.PhoneNumber?.trim() || null,
        Description: values.Description?.trim() || null,
      });

      if (isNewRole) {
        toast.success('Essential role successfully added.');
      }

      onRequestClose();
    } catch {
      // upsertError is shown in the modal
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} aria={{ labelledby: 'essential-role-modal' }}>
      <ModalHeader as="h2" id="essential-role-modal">
        {isNewRole ? 'Add Essential Roles' : 'Edit Essential Roles'}
      </ModalHeader>
      <ModalCloseButton onClick={onRequestClose} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          {upsertError && <Alert variant="error" message={upsertError.message} mb="24" />}
          <Input
            label="Name"
            {...register('Name')}
            formVariant={errors.Name ? 'error' : undefined}
            helperText={errors.Name?.message}
            mb="16"
          />

          <Box mb="16">
            <Select
              label="Role"
              formVariant={errors.RoleName ? 'error' : undefined}
              helperText={errors.RoleName?.message}
              isCreatable
              isClearable
              options={predefinedSelectOptions}
              value={roleSelectValue}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              placeholder="Select Role / Add Role"
              onChange={opt => {
                if (!opt) {
                  setValue('PredefinedKey', '', { shouldValidate: true });
                  setValue('RoleName', '', { shouldValidate: true });
                  setValue('Description', '');
                  return;
                }
                if (isPredefinedOption(opt)) {
                  setValue('PredefinedKey', opt.predefinedKey, { shouldValidate: true });
                  setValue('RoleName', opt.label, { shouldValidate: true });
                  setValue('Description', opt.description);
                } else {
                  setValue('PredefinedKey', '', { shouldValidate: true });
                  setValue('RoleName', opt.label.trim(), { shouldValidate: true });
                  setValue('Description', '');
                }
              }}
            />
          </Box>

          <Textarea label="Role Description" {...register('Description')} rows={4} mb="16" />

          <Input
            label="Email"
            type="email"
            autoComplete="email"
            placeholder="sample@email.com"
            {...register('Email')}
            formVariant={errors.Email ? 'error' : undefined}
            helperText={errors.Email?.message}
            mb="16"
          />

          <Input
            label="Phone"
            type="tel"
            placeholder="(555)555-5555"
            {...register('PhoneNumber')}
            formVariant={errors.PhoneNumber ? 'error' : undefined}
            helperText={errors.PhoneNumber?.message}
          />
        </ModalBody>

        <ModalFooter>
          <Button variant="subtleInactive" type="button" onClick={onRequestClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isUpserting} ml="8">
           Save
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
